import axios from "axios";
const axiosRetry = require("axios-retry").default;
// import authService from "@/services/auth";
// IMPORTANT: Import the store instance. Make sure store is initialized before this runs heavily.
//            Consider structuring your app initialization (main.js) carefully.
import store from "@/store"; // Adjust path as needed
import hostConfig from "@/config/hostResolver";

// --- Configuration for Budget Microservice ---
const budgetService = axios.create({
  baseURL: process.env.VUE_APP_BUDGET_URL ?? `${hostConfig.apiBaseUrl}/budget-service`,
  timeout: 20000, // 20 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// --- Universal Token Handling Interceptor ---
// Pass `skipAuth: true` in a request's config for public endpoints (e.g. pre-login
// lookups) so no session credentials are attached.
const authenticationHandler = (config) => {
  if (config.skipAuth) return config;
  const token = store.state.token;
  const magicLinkToken = store.state.magicLinkToken;
  // const token = authService.getAuthToken();
  // const magicLinkToken = authService.getMagicLinkToken();
  if (magicLinkToken || token) config.headers.Authorization = "Bearer " + (magicLinkToken || token);
  if (magicLinkToken) config.headers["x-whistle-oauth"] = 1;

  return config;
};

// --- Universal Error Handling Interceptor ---
const errorHandler = (error) => {
  console.log("API Error:", error); // Log the raw error for debugging

  let title = "Error";
  let message = "An unexpected error occurred. Please try again later.";
  let statusCode = null;

  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    statusCode = error.response.status;
    title = `Error ${statusCode}`;
    // Try to get a meaningful error message from the backend response
    message =
      (error?.config && error?.config?.errorContext) ||
      error.response.data?.message ||
      error.response.data?.error ||
      error.response.statusText ||
      message;

    // Handle specific status codes globally if needed
    if (statusCode === 401) {
      message = "Authentication failed. Please log in again.";
      // TODO: Redirect to login, refresh token, etc.
      // Example: router.push('/login');
    } else if (statusCode === 403) {
      message = "You do not have permission to perform this action.";
    } else if (statusCode === 404) {
      message = "The requested resource was not found.";
    }
    // else if (statusCode >= 500) {
    //   title = "Server Error";
    //   message = "A problem occurred on our servers. Please try again later.";
    // }
  } else if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser
    title = "Network Error";
    message = "Could not connect to the server. Please check your network connection.";
  } else {
    // Something happened in setting up the request that triggered an Error
    title = "Request Setup Error";
    message = error.message || message;
  }

  // If the error is silent, don't show the error notification
  if (error?.config?.silent !== true) {
    store.dispatch("showError", { title, message });
  }

  // IMPORTANT: Reject the promise so the calling code's .catch() still works
  return Promise.reject(error);
};

// --- Apply Interceptors to Each Service Instance ---
budgetService.interceptors.request.use(authenticationHandler);
budgetService.interceptors.response.use((response) => response, errorHandler);

// --- Easily create an instance for each service
const createAxiosInstance = (baseURL, timeout = 20000, retries = 3) => {
  const service = axios.create({
    baseURL,
    timeout, // 20 seconds timeout
    headers: {
      "Content-Type": "application/json",
    },
  });

  service.interceptors.request.use(authenticationHandler);
  service.interceptors.response.use((response) => response, errorHandler);

  // Configure retry behavior for transient failures
  axiosRetry(service, {
    retries,
    retryDelay: axiosRetry.exponentialDelay, // Exponential backoff
    shouldResetTimeout: true,
    retryCondition: (error) => {
      // Retry on connection errors and 5xx responses
      return (
        // They can explicitly disable retries by setting retryRequest to false
        error?.config?.retryRequest !== false &&
        (axiosRetry.isNetworkOrIdempotentRequestError(error) ||
          error.code === "ECONNRESET" ||
          error.code === "ETIMEDOUT" ||
          error.code === "ECONNABORTED" ||
          (error.response && error.response.status >= 500))
      );
    },
    onRetry: (retryCount, error, requestConfig) => {
      console.log(
        `Retry attempt #${retryCount} for ${requestConfig.url} due to ${
          error.code || error.message
        }`
      );
    },
  });
  return service;
};
// --- Export the configured instances ---
export { budgetService, createAxiosInstance };
